import { Link } from "react-router";

type WorkWithMeProps = {
  email?: string; 
  portfolioUrl?: string; 
  showBackground?: boolean; 
};

export default function WorkWithMeSection({
  email = "hello@jaseowns.com",
  portfolioUrl = "/dev",
  showBackground = false,
}: WorkWithMeProps) {
  return (
    <section
      className={[
        "relative",
        showBackground
          ? "bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
          : "bg-transparent",
        "py-16 px-0 sm:px-4 lg:px-8",
      ].join(" ")}
      aria-labelledby="work-with-me-title"
    >
      <div className="mx-auto max-w-5xl">
        {/* Card */}
        <div className="relative overflow-hidden sm:rounded-3xl border border-white/10 bg-slate-900/70 shadow-2xl">
          {/* Accent Ring */}
          <div className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-tr from-cyan-500/20 via-blue-500/10 to-purple-500/20 blur-2xl" />

          <div className="relative z-10 grid gap-10 p-8 sm:p-12 lg:grid-cols-5 lg:gap-12">
            {/* Header + Intro */}
            <div className="lg:col-span-3">
              <h2
                id="work-with-me-title"
                className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl"
              >
                <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-indigo-300 bg-clip-text text-transparent">
                  Work With Me
                </span>
              </h2>

              <p className="mt-4 text-slate-300 leading-relaxed">
                I'm <span className="font-semibold text-white">Jason Ramsey</span> — a
                full-stack engineer with 21 years of experience building and
                migrating large-scale platforms for brands like Match Group. I
                specialize in modernizing legacy systems and shipping polished,
                maintainable products quickly.
              </p>

              {/* Specialties */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  "ColdFusion support & platform migration",
                  "Full-stack development (CF, .NET, SQL, React, Remix, Node, Tailwind)",
                  "Custom automation & developer tools",
                  "Streaming & gaming integrations (bots, APIs)",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckIcon className="mt-1 h-5 w-5 flex-none text-cyan-400" />
                    <p className="text-slate-200">{item}</p>
                  </div>
                ))}
              </div>

              {/* Why work with me */}
              <div className="mt-8 rounded-2xl border border-white/10 bg-slate-800/50 p-5">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                  Why work with me
                </h3>
                <ul className="mt-3 space-y-2">
                  {[
                    "Proven migrations: led cross-functional teams to modernize platforms",
                    "Senior leadership: from Lead Engineer to Staff Engineer & Manager",
                    "Speed + clarity: fast iterations, proactive communication",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <SparkleIcon className="mt-1 h-5 w-5 flex-none text-indigo-400" />
                      <span className="text-slate-200">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Rates note (revised) */}
              <div className="mt-6 text-slate-300">
                <p className="leading-relaxed">
                  <span className="font-semibold text-white">Rates:</span> Every
                  project is unique. I offer both <span className="font-medium">fixed-price packages</span> and
                  hourly consulting. <span className="font-medium">Rates depend on project scope</span> so we can
                  match the engagement to your goals and timeline.
                </p>
              </div>

              {/* CTA buttons */}
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to={`mailto:${email}?subject=Hello`}
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-cyan-500 px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-cyan-500/20 transition hover:translate-y-[-1px] hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  <MailIcon className="mr-2 h-5 w-5" /> Contact Me
                </Link>
                <Link
                  to={portfolioUrl}
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:translate-y-[-1px] hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  <ExternalIcon className="mr-2 h-5 w-5" /> View Dev Projects
                </Link>
              </div>
            </div>

            {/* Side Panel / At-a-glance */}
            <aside className="lg:col-span-2">
              <div className="h-full sm:rounded-2xl sm:border sm:border-white/10 sm:bg-slate-800/40 sm:p-6">
                <h3 className="text-base font-semibold text-white">Currently booking</h3>
                <ul className="mt-4 space-y-3">
                  {[
                    {
                      label: "ColdFusion support & migrations",
                      desc: "Assess, plan, and execute migrations with zero-downtime strategies.",
                    },
                    {
                      label: "Full-stack app builds",
                      desc: "React/Remix frontends, .NET/Node APIs, SQLite/MSSQL, Tailwind UI.",
                    },
                    {
                      label: "Automation & tools",
                      desc: "Developer productivity, content pipelines, log viewing, scripting utilities.",
                    },
                    {
                      label: "Streaming & gaming integrations",
                      desc: "Bots, overlays, Razor scripting support, platform APIs.",
                    },
                  ].map((item) => (
                    <li key={item.label} className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
                      <p className="font-medium text-slate-100">{item.label}</p>
                      <p className="mt-1 text-sm text-slate-300">{item.desc}</p>
                    </li>
                  ))}
                </ul>

                {/* Badges */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {["AI", "ColdFusion",".NET Core", "React", "Remix", "Node JS", "API", "CSS", "Tailwind", "JQuery", "MSSQL", "SQLite"].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-200"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
                <p className="my-4 font-semibold text-white">
                <Link
                  to={`mailto:${email}?subject=Project%20inquiry`}
                  className="w-full sm:w-auto hover:underline"
                >
                  Not seeing what you need? Let me know!
                </Link>
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Inline SVG Icons (no external deps) ---
function CheckIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M9 12.75l2 2 4-4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.2" opacity=".35" />
    </svg>
  );
}

function SparkleIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 3l1.6 3.8L17 8.4l-3.4 1.6L12 14l-1.6-4-3.4-1.6 3.4-1.6L12 3z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="currentColor"
        opacity=".25"
      />
      <path d="M6 18l.8 1.8L8.6 21l-1.8.8L6 24l-.8-2.2L3.4 21l1.8-.8L6 18z" fill="currentColor" opacity=".35" />
    </svg>
  );
}

function MailIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M3 6.75A1.75 1.75 0 014.75 5h14.5A1.75 1.75 0 0121 6.75V17a2 2 0 01-2 2H5a2 2 0 01-2-2V6.75z" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4 7l8 5.5L20 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ExternalIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M14 4h6v6M10 14L20 4M20 14v5.25A.75.75 0 0119.25 20H5a1 1 0 01-1-1V4.75A.75.75 0 014.75 4H10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


// =============================
// PackagesSection (named export)
// =============================
export function PackagesSection({
    email = "hello@jaseowns.com",
    ctaLabel = "Contact to Start",
  }: {
    email?: string;
    ctaLabel?: string;
  }) {
    const packages = [
      {
        name: "Starter Site",
        price: "Fixed-price",
        blurb: "Launch a clean, fast marketing site with contact form and analytics.",
        idealFor: "Solo founders, small businesses",
        includes: [
          "1-3 pages (Home, About, Contact)",
          "Remix + Tailwind, deployed on Fly.io",
          "Contact form wiring + basic SEO",
          "Light brand styling + responsive layout",
        ],
        badge: "Most accessible",
      },
      {
        name: "Migration Sprint",
        price: "Fixed-price",
        blurb: "Plan and execute a focused ColdFusion → .NET Core modernization sprint.",
        idealFor: "Teams with legacy systems",
        includes: [
          "Assessment + migration roadmap",
          "Strangler-fig pattern for zero-downtime rollout",
          ".NET API scaffolding + auth integration",
          "CI build + deployment playbook",
        ],
        featured: true,
        badge: "Best value",
      },
      {
        name: "Automation Toolkit",
        price: "Fixed-price",
        blurb: "Custom scripts and internal tools that cut manual work for devs/ops.",
        idealFor: "Product teams, creators, streamers",
        includes: [
          "Task + data pipelines (Node)",
          "Content/clip utilities & dashboards",
          "Streaming/chatbot integrations",
          "Documentation + handoff session",
        ],
        badge: "Team favorite",
      },
    ];
  
    return (
      <section className="relative py-16 px-4 sm:px-6 lg:px-8" aria-labelledby="packages-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="packages-title" className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
              Fixed-Price Packages
            </span>
          </h2>
          <p className="mt-3 max-w-3xl text-slate-300">
            Clear outcomes. Predictable costs. We'll scope your needs and lock pricing before work begins.
          </p>
  
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((p) => (
              <div
                key={p.name}
                className={[
                  "relative overflow-hidden rounded-2xl border bg-slate-900/70 p-6",
                  "border-white/10 shadow-xl",
                  p.featured ? "ring-1 ring-cyan-400/40" : "",
                ].join(" ")}
              >
                {p.badge && (
                  <span className="absolute right-3 top-3 rounded-full border border-white/10 bg-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/90">
                    {p.badge}
                  </span>
                )}
                <h3 className="text-lg font-bold text-white">{p.name}</h3>
                <p className="mt-1 text-sm text-slate-300">{p.blurb}</p>
                <p className="mt-2 text-xs text-slate-400">Ideal for: {p.idealFor}</p>
                <p className="mt-4 text-sm font-semibold text-slate-200">{p.price}</p>
  
                <ul className="mt-4 space-y-2">
                  {p.includes.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckIcon className="mt-0.5 h-4 w-4 text-emerald-400" />
                      <span className="text-sm text-slate-200">{item}</span>
                    </li>
                  ))}
                </ul>
  
                <div className="mt-6">
                  <Link
                    to={`mailto:${email}?subject=${encodeURIComponent(p.name + " inquiry")}`}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-emerald-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-md shadow-emerald-400/20 transition hover:translate-y-[-1px] hover:bg-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {ctaLabel}
                  </Link>
                </div>
              </div>
            ))}
          </div>
  
          <p className="mt-8 text-sm text-slate-400">
            Looking for something different? I'll tailor a package to your goals and constraints. <Link className="text-cyan-300 underline decoration-cyan-400/50 underline-offset-4 hover:text-cyan-200" to={`mailto:${email}`}>Email me</Link> and we'll scope it in one call.
          </p>
        </div>
      </section>
    );
  }
  